import React, { useState, useEffect } from 'react'
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import ThemeColorSelector from './ThemeColorSelector';
import { applyTheme } from '../helper/themeHelper';
import config from '../config';
import { useAuth } from '../context/AuthContext';

const ThemeLayer = () => {
    const { auth } = useAuth();
    const [previewImage1, setPreviewImage1] = useState('');
    const [previewImage2, setPreviewImage2] = useState('');
    const [previewImage3, setPreviewImage3] = useState('');
    
    const [logoFile, setLogoFile] = useState(null);
    const [faviconFile, setFaviconFile] = useState(null);
    const [authBgFile, setAuthBgFile] = useState(null);

    const [siteTitle, setSiteTitle] = useState('Vscroll Dashboard');
    const [themeColor, setThemeColor] = useState(localStorage.getItem('themeColor') || 'blue');
    const [maintenanceMode, setMaintenanceMode] = useState(false);

    useEffect(() => {
        fetch(`${config.API_BASE_URL}/api/settings`)
            .then(res => res.json())
            .then(data => {
                if (data.site_title) setSiteTitle(data.site_title);
                if (data.theme_color) setThemeColor(data.theme_color);
                if (data.maintenance_mode !== undefined) setMaintenanceMode(!!data.maintenance_mode);
                
                if (data.logo_url) {
                    setPreviewImage1(data.logo_url.startsWith('http') ? data.logo_url : `${config.API_BASE_URL}${data.logo_url}`);
                }
                if (data.favicon_url) {
                    setPreviewImage2(data.favicon_url.startsWith('http') ? data.favicon_url : `${config.API_BASE_URL}${data.favicon_url}`);
                }
                if (data.auth_bg_url) {
                    setPreviewImage3(data.auth_bg_url.startsWith('http') ? data.auth_bg_url : `${config.API_BASE_URL}${data.auth_bg_url}`);
                }
            })
            .catch(err => console.error('Failed to fetch settings:', err));
    }, []);

    useEffect(() => {
        applyTheme(themeColor);
    }, [themeColor]);

    const readURL = (input, setPreviewImage, setFile) => {
        if (input.target.files && input.target.files[0]) {
            const file = input.target.files[0];
            setFile(file);
            const reader = new FileReader();
            reader.onload = function (e) {
                setPreviewImage(e.target.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        const formData = new FormData();
        formData.append('site_title', siteTitle);
        formData.append('theme_color', themeColor);
        formData.append('maintenance_mode', maintenanceMode);
        
        if (logoFile) formData.append('logo', logoFile);
        if (faviconFile) formData.append('favicon', faviconFile);
        if (authBgFile) formData.append('auth_bg', authBgFile);
        
        // Keep existing URLs if no new file uploaded (backend should handle this logic, 
        // but since we are sending FormData, we might need to handle it on backend or send url if file is null)
        // Actually, let's just send the text fields. If file is present, backend updates it.
        // If we want to persist existing URLs when no new file is uploaded, we don't send anything for that file field.
        // However, we also have existing URLs in previewImage variables if we need them.
        // But for file upload inputs, we usually only send if there's a new file.
        // Let's ensure backend doesn't wipe out existing URLs if no file is sent.

        auth.fetch(`${config.API_BASE_URL}/api/settings`, {
            method: 'POST',
            body: formData
            // Note: Content-Type header should NOT be set manually for FormData, fetch handles it
        })
        .then(res => {
            if (!res.ok) throw new Error('Failed to update settings');
            return res.json();
        })
        .then(data => {
            toast.success('Settings saved successfully!');
            // Update previews with returned URLs if available
            if (data.logo_url) setPreviewImage1(data.logo_url);
            if (data.favicon_url) setPreviewImage2(data.favicon_url);
            if (data.auth_bg_url) setPreviewImage3(data.auth_bg_url);
            
            // Clear file inputs
            setLogoFile(null);
            setFaviconFile(null);
            setAuthBgFile(null);

            // Re-apply theme immediately
            applyTheme(themeColor);
            // Notify App.js to update title and favicon
            window.dispatchEvent(new Event('settingsChanged'));
        })
        .catch(err => {
            console.error(err);
            toast.error(err.message || 'Failed to save settings.');
        });
    };

    return (
        <div className="card h-100 p-0 radius-12">
            <div className="card-body p-24">
                <form onSubmit={handleSubmit}>
                    <div className="row gy-4">
                        <div className="col-12">
                            <label
                                htmlFor="siteTitle"
                                className="form-label fw-semibold text-secondary-light text-md mb-8"
                            >
                                Site Title
                            </label>
                            <input
                                type="text"
                                className="form-control radius-8"
                                id="siteTitle"
                                placeholder="Enter Site Title"
                                value={siteTitle}
                                onChange={(e) => setSiteTitle(e.target.value)}
                            />
                        </div>

                        <div className="col-md-6">
                            <label
                                htmlFor="imageUpload"
                                className="form-label fw-semibold text-secondary-light text-md mb-8"
                            >
                                Logo <span className="text-secondary-light fw-normal">(140px X 140px)</span>
                            </label>
                            <input
                                type="file"
                                className="form-control radius-8"
                                id="imageUpload"
                                onChange={(e) => readURL(e, setPreviewImage1, setLogoFile)}
                            />
                            <div className="avatar-upload mt-16">
                                <div className="avatar-preview style-two">
                                    <div
                                        id="previewImage1"
                                        style={{
                                            backgroundImage: `url(${previewImage1})`,

                                        }}
                                    ></div>
                                </div>
                            </div>
                        </div>

                        <div className="col-md-6">
                            <label
                                htmlFor="imageUploadTwo"
                                className="form-label fw-semibold text-secondary-light text-md mb-8"
                            >
                                Favicon <span className="text-secondary-light fw-normal">(Any Size)</span>
                            </label>
                            <input
                                type="file"
                                className="form-control radius-8"
                                id="imageUploadTwo"
                                onChange={(e) => readURL(e, setPreviewImage2)}
                            />
                            <div className="avatar-upload mt-16">
                                <div className="avatar-preview style-two">
                                    <div
                                        id="previewImage2"
                                        style={{
                                            backgroundImage: `url(${previewImage2})`,

                                        }}
                                    ></div>
                                </div>
                            </div>
                        </div>

                        <div className="col-md-6">
                            <label
                                htmlFor="imageUploadThree"
                                className="form-label fw-semibold text-secondary-light text-md mb-8"
                            >
                                Auth Background <span className="text-secondary-light fw-normal">(917px X 917px)</span>
                            </label>
                            <input
                                type="file"
                                className="form-control radius-8"
                                id="imageUploadThree"
                                onChange={(e) => readURL(e, setPreviewImage3, setAuthBgFile)}
                            />
                            <div className="avatar-upload mt-16">
                                <div className="avatar-preview style-two">
                                    <div
                                        id="previewImage3"
                                        style={{
                                            backgroundImage: `url(${previewImage3})`,

                                        }}
                                    ></div>
                                </div>
                            </div>
                        </div>

                        <div className="col-12 mt-4">
                            <div className="d-flex align-items-center gap-3 p-3 border radius-8 bg-light">
                                <div className="form-check form-switch mb-0">
                                    <input 
                                        className="form-check-input" 
                                        type="checkbox" 
                                        role="switch" 
                                        id="maintenanceMode" 
                                        checked={maintenanceMode} 
                                        onChange={(e) => setMaintenanceMode(e.target.checked)} 
                                        style={{ width: '3em', height: '1.5em', cursor: 'pointer' }}
                                    />
                                </div>
                                <div>
                                    <label className="form-check-label fw-bold text-md cursor-pointer" htmlFor="maintenanceMode">
                                        Maintenance Mode
                                    </label>
                                    <p className="text-secondary-light text-sm mb-0">
                                        When enabled, only administrators can access the system. All other users will be blocked.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                    <ThemeColorSelector themeColor={themeColor} setThemeColor={setThemeColor} />
                    <div className="d-flex align-items-center justify-content-center gap-3 mt-24">
                            <button
                                type="reset"
                                className="border border-danger-600 bg-hover-danger-200 text-danger-600 text-md px-40 py-11 radius-8"
                                onClick={() => {
                                    setSiteTitle('Vscroll Dashboard');
                                    setThemeColor('blue');
                                    setPreviewImage1('');
                                    setPreviewImage2('');
                                    setPreviewImage3('');
                                }}
                            >
                                Reset
                            </button>
                            <button
                                type="submit"
                                className="btn btn-primary border border-primary-600 text-md px-24 py-12 radius-8"
                            >
                                Save Change
                            </button>
                        </div>
                </form>
            </div>
        </div>
    )
}

export default ThemeLayer
